'use client'

import { memo } from 'react'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Montserrat_Underline } from '@/app/fonts/google-font-fallbacks'
import { CMS_DEMO_ASSET_URLS } from '@/lib/cms/demo-assets'
import Image from 'next/image'
import {
  ArrowRight,
  Plus,
  Terminal,
  Globe,
  ShieldCheck,
  Zap,
  Monitor,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  RotateCcw,
  RotateCw,
  Pause,
  Play,
  Mail,
  Calendar,
  Heart,
  FileText,
  TrendingUp,
  Wallet,
  MessageSquare,
  LineChart,
  Inbox,
  Twitter,
  Apple,
  AppWindow,
  Smartphone,
  CheckCircle2,
  Download
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useState, useRef, useCallback } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

// baby: NINX download API - query-based (single serverless function on Vercel)
const NINX_DOWNLOAD_API = '/api/ninx/download'

// Montserrat Underline font for NINX branding
const montserratSubrayada = Montserrat_Underline({
  weight: ['400', '700'],
  subsets: ['latin'],
  display: 'swap',
  // baby: No capsize metrics for "Montserrat Underline" key in Next.js (legacy: Subrayada); skip fallback CSS tuning
  adjustFontFallback: false,
})

// Grid background component
const GridPattern = () => (
  <div className="absolute inset-0 -z-10 h-full w-full bg-white pointer-events-none">
    <div className="absolute h-full w-full bg-[linear-gradient(to_right,#f1f5f9_1px,transparent_1px),linear-gradient(to_bottom,#f1f5f9_1px,transparent_1px)] bg-[size:6rem_6rem]" />
    {/* Hero glow - same as Ningenie landing */}
    <div className="absolute right-1/2 h-80 w-[120%] translate-x-1/2 rounded-[60%] bg-[#21728d]/20 blur-3xl" />
  </div>
)

// Circuit SVG
const CircuitBackground = () => (
  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1200px] h-[800px] -z-1 pointer-events-none opacity-30">
    <svg width="100%" height="100%" viewBox="0 0 1200 800" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="trace_grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#cbd5e1" stopOpacity="0.2" />
          <stop offset="50%" stopColor="#94a3b8" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#cbd5e1" stopOpacity="0.2" />
        </linearGradient>
      </defs>

      {/* Top Left Traces */}
      <path d="M 450 300 L 400 250 L 200 250" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="200" cy="250" r="3" fill="#cbd5e1" />
      <path d="M 480 300 L 440 220 L 300 220" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="300" cy="220" r="3" fill="#cbd5e1" />
      <path d="M 500 280 L 500 150 L 400 150" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="400" cy="150" r="3" fill="#cbd5e1" />

      {/* Top Right Traces */}
      <path d="M 750 300 L 800 250 L 1000 250" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="1000" cy="250" r="3" fill="#cbd5e1" />
      <path d="M 720 300 L 760 220 L 900 220" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="900" cy="220" r="3" fill="#cbd5e1" />
      <path d="M 700 280 L 700 150 L 800 150" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="800" cy="150" r="3" fill="#cbd5e1" />

      {/* Bottom Left Traces */}
      <path d="M 450 500 L 400 550 L 200 550" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="200" cy="550" r="3" fill="#cbd5e1" />
      <path d="M 480 500 L 440 580 L 300 580" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="300" cy="580" r="3" fill="#cbd5e1" />
      <path d="M 500 520 L 500 650 L 400 650" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="400" cy="650" r="3" fill="#cbd5e1" />

      {/* Bottom Right Traces */}
      <path d="M 750 500 L 800 550 L 1000 550" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="1000" cy="550" r="3" fill="#cbd5e1" />
      <path d="M 720 500 L 760 580 L 900 580" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="900" cy="580" r="3" fill="#cbd5e1" />
      <path d="M 700 520 L 700 650 L 800 650" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="800" cy="650" r="3" fill="#cbd5e1" />
    </svg>
  </div>
)

// NINX Features: app integrations first, then desktop extras (shell, system control, connect, remote)
const NINX_FEATURES = [
  // App integrations
  {
    step: 'Apps',
    title: 'Gmail',
    description: 'Search, organize, and respond to emails with simple commands. Genie reads your inbox, filters important messages, and drafts replies.',
    icon: Mail,
    color: '#EA4335',
    demoSrc: CMS_DEMO_ASSET_URLS.gmail,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  },
  {
    step: 'Apps',
    title: 'Calendar',
    description: 'Check your schedule, create events, and get reminders. Just ask "What\'s on my calendar today?" and Genie shows your day at a glance.',
    icon: Calendar,
    color: '#4285F4',
    demoSrc: CMS_DEMO_ASSET_URLS.calendar,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  },
  {
    step: 'Apps',
    title: 'Twitter / X',
    description: 'Draft tweets, schedule posts, and manage your social presence. Genie helps you craft engaging content and post at optimal times.',
    icon: Twitter,
    color: '#1DA1F2',
    demoSrc: CMS_DEMO_ASSET_URLS.twitterX,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  },
  // {
  //   step: 'Apps',
  //   title: 'Health',
  //   description: 'Track symptoms, set health reminders, and get personalized insights. Genie integrates with your health apps to keep you informed.',
  //   icon: Heart,
  //   color: '#FF6B6B',
  //   demoSrc: CMS_DEMO_ASSET_URLS.health,
  //   posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  // },
  {
    step: 'Apps',
    title: 'Notion',
    description: 'Search, summarize, and organize your documents. Genie can find that important file and extract key information for you.',
    icon: FileText,
    color: '#7C3AED',
    demoSrc: CMS_DEMO_ASSET_URLS.gmail,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  },
  {
    step: 'Apps',
    title: 'Groww & Brokers',
    description: 'View positions, check watchlists, and get market insights. Genie brings your portfolio data right to the chat.',
    icon: TrendingUp,
    color: '#387ED1',
    demoSrc: CMS_DEMO_ASSET_URLS.groww,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  },
  {
    step: 'Apps',
    title: 'Binance',
    description: 'Check crypto balances, view market data, and monitor your Binance portfolio. Genie shows your holdings in real-time.',
    icon: Wallet,
    color: '#F0B90B',
    demoSrc: CMS_DEMO_ASSET_URLS.groww,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  },
  {
    step: 'Apps',
    title: 'Contacts',
    description: 'Search your contacts, share contact info, and manage your address book with simple commands. Genie finds who you need instantly.',
    icon: MessageSquare,
    color: '#25D366',
    demoSrc: CMS_DEMO_ASSET_URLS.gmail,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  },
  // Desktop extras: shell, system control, connect to app, remote control
  {
    step: 'Desktop',
    title: 'System Control',
    description: 'Open apps, manage files, and control your desktop. Tell Genie what you need and it handles the rest.',
    icon: Monitor,
    color: '#3b82f6',
    demoSrc: CMS_DEMO_ASSET_URLS.systemControl,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  },
  {
    step: 'Desktop',
    title: 'Local Shell',
    description: 'Run commands on your computer from the chat. Search files, open folders, and automate tasks: all through natural language.',
    icon: Terminal,
    color: '#10b981',
    demoSrc: CMS_DEMO_ASSET_URLS.gmail,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  },
  {
    step: 'Desktop',
    title: 'Connect to App',
    description: 'One account across web, mobile, and desktop. Start a task on your phone, let your desktop finish it. Seamlessly in sync.',
    icon: Globe,
    color: '#8b5cf6',
    demoSrc: CMS_DEMO_ASSET_URLS.health,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  },
  {
    step: 'Desktop',
    title: 'Remote Control',
    description: 'Control your desktop from anywhere. Connect securely and run tasks on your home or office PC: no port forwarding needed.',
    icon: ShieldCheck,
    color: '#6366f1',
    demoSrc: CMS_DEMO_ASSET_URLS.gmail,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  }
]

// Feature Carousel Component
const FeatureCarousel = memo(function FeatureCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loadedVideos, setLoadedVideos] = useState<Set<number>>(new Set())
  const [failedVideos, setFailedVideos] = useState<Set<number>>(new Set())
  const [isPlaying, setIsPlaying] = useState(true)
  const [isHovering, setIsHovering] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? NINX_FEATURES.length - 1 : prev - 1))
    setIsPlaying(true)
  }, [])

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === NINX_FEATURES.length - 1 ? 0 : prev + 1))
    setIsPlaying(true)
  }, [])

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index)
    setIsPlaying(true)
  }, [])

  const handleVideoCanPlay = useCallback((index: number) => {
    setLoadedVideos((prev) => new Set(prev).add(index))
    setFailedVideos((prev) => {
      const next = new Set(prev)
      next.delete(index)
      return next
    })
  }, [])

  const handleVideoError = useCallback((index: number) => {
    setFailedVideos((prev) => new Set(prev).add(index))
  }, [])

  const togglePlayPause = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }, [isPlaying])

  const seekBackward = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 5)
    }
  }, [])

  const seekForward = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.min(
        videoRef.current.duration ?? 0,
        videoRef.current.currentTime + 5
      )
    }
  }, [])

  const isCurrentVideoLoaded = loadedVideos.has(currentIndex)
  const isCurrentVideoFailed = failedVideos.has(currentIndex)
  const currentFeature = NINX_FEATURES[currentIndex]
  const IconComponent = currentFeature.icon

  return (
    <div className="grid gap-8 md:gap-12 lg:grid-cols-[1fr_1.2fr] lg:items-center">
      <div className="flex flex-col gap-6 order-2 lg:order-1">
        <div className="flex items-center gap-3">
          <div
            className="flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-100 shadow-sm"
            style={{ backgroundColor: `${currentFeature.color}15` }}
          >
            <IconComponent className="h-6 w-6" style={{ color: currentFeature.color }} />
          </div>
          <span className="text-sm font-bold uppercase tracking-wider text-slate-500 bg-slate-50 px-2 py-1 rounded-md border border-slate-100">
            {currentFeature.step}
          </span>
        </div>

        <h3 className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl leading-tight">
          {currentFeature.title}
        </h3>

        <p className="text-base text-slate-600 md:text-lg leading-relaxed">
          {currentFeature.description}
        </p>

        <div className="flex items-center gap-2 mt-2">
          {NINX_FEATURES.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-1.5 rounded-full transition-all duration-300 ${currentIndex === index
                ? 'bg-[#21728d] w-6'
                : 'bg-slate-200 hover:bg-slate-300 w-2'
                }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        <div className="flex items-center gap-3 mt-2">
          <Button
            variant="outline"
            size="icon"
            onClick={goToPrevious}
            className="h-12 w-12 rounded-xl border-slate-200 hover:bg-slate-50 hover:border-[#21728d] bg-[#88a1ac]/10"
          >
            <ChevronLeft className="h-5 w-5 text-[#21728d]" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={goToNext}
            className="h-12 w-12 rounded-xl border-slate-200 hover:bg-slate-50 hover:border-[#21728d] bg-[#88a1ac]/10"
          >
            <ChevronRight className="h-5 w-5 text-[#21728d]" />
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-center order-1 lg:order-2">
        <div className="relative w-full max-w-[500px]">
          {/* Desktop Frame - Light */}
          <div className="relative rounded-xl border border-slate-200 bg-white p-2 shadow-2xl shadow-slate-200/50">
            <div
              className="relative rounded-lg border border-slate-200 bg-slate-50 overflow-hidden aspect-video"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              {!isCurrentVideoFailed && (
                <video
                  ref={videoRef}
                  key={currentFeature.demoSrc}
                  className="absolute inset-0 w-full h-full object-cover z-10"
                  src={currentFeature.demoSrc}
                  poster={currentFeature.posterSrc}
                  autoPlay
                  muted
                  loop
                  playsInline
                  onCanPlay={() => handleVideoCanPlay(currentIndex)}
                  onError={() => handleVideoError(currentIndex)}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                />
              )}

              {isCurrentVideoLoaded && !isCurrentVideoFailed && (
                <div
                  className={`absolute inset-0 z-30 flex items-center justify-center bg-black/40 transition-opacity duration-300 ${isHovering ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                >
                  <div className="flex items-center gap-4">
                    <button
                      onClick={seekBackward}
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-all hover:bg-white/30 hover:scale-110 border border-white/10"
                      aria-label="Rewind 5 seconds"
                    >
                      <RotateCcw className="h-5 w-5" />
                    </button>
                    <button
                      onClick={togglePlayPause}
                      className="flex h-14 w-14 items-center justify-center rounded-full bg-white/30 text-white backdrop-blur-sm transition-all hover:bg-white/40 hover:scale-110 border border-white/10"
                      aria-label={isPlaying ? 'Pause' : 'Play'}
                    >
                      {isPlaying ? <Pause className="h-7 w-7" /> : <Play className="h-7 w-7 ml-1" />}
                    </button>
                    <button
                      onClick={seekForward}
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-all hover:bg-white/30 hover:scale-110 border border-white/10"
                      aria-label="Forward 5 seconds"
                    >
                      <RotateCw className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}

              {(!isCurrentVideoLoaded || isCurrentVideoFailed) && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 z-0">
                  <div
                    className="flex h-20 w-20 items-center justify-center rounded-2xl mb-4 border border-slate-200"
                    style={{ backgroundColor: `${currentFeature.color}15` }}
                  >
                    <IconComponent className="h-10 w-10" style={{ color: currentFeature.color }} />
                  </div>
                  <span className="text-sm font-medium text-slate-500">
                    {isCurrentVideoFailed ? 'Video unavailable' : 'Loading...'}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div
            className="absolute -inset-10 -z-10 rounded-full blur-3xl opacity-20"
            style={{ backgroundColor: currentFeature.color }}
          />
        </div>
      </div>
    </div>
  )
})

// Dynamically import Lottie
const Lottie = dynamic(() => import('lottie-react'), { ssr: false })

const fetchAnimationData = async (): Promise<Record<string, unknown>> => {
  const response = await fetch('/jsons/ninx_ai_chat_lottie.json')
  if (!response.ok) {
    throw new Error(`Failed to load chat AI animation: ${response.status}`)
  }
  return response.json()
}

const FAQ_ITEMS = [
  {
    question: "What is NINX?",
    answer: "NINX is the desktop agent that runs locally on your machine. It connects to Gmail, Calendar, Brokers, and more: plus gives you local shell, system control, and remote access. The app (web and mobile) connects to NINX for a seamless experience."
  },
  {
    question: "Is it secure?",
    answer: "Yes. NINX is CASA + Certified, and our SOC 2 Type II audit is in progress. On-device work stays on your system. If you use cloud credits, providers run under a Zero Data Retention (ZDR) policy. Sensitive operations require your confirmation."
  },
  {
    question: "Does it work with the app?",
    answer: "Absolutely. NINX connects seamlessly with the web and mobile app. Start a task on your phone, let your desktop finish it: one account, seamlessly in sync."
  }
]

// baby: Fetch available NINX download platforms (macArm64, macX64, winX64)
async function fetchNinxPlatforms() {
  const res = await fetch(`${NINX_DOWNLOAD_API}?format=json`)
  if (!res.ok) return { macArm64: false, macX64: false, winX64: false }
  return res.json() as Promise<{ macArm64: boolean; macX64: boolean; winX64: boolean }>
}

const NinxLandingPage = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null)
  const [downloadSuccess, setDownloadSuccess] = useState<'mac-arm64' | 'mac-x64' | null>(null)
  const goToPlansPage = useCallback(() => {
    window.location.href = '/ningenie/plans'
  }, [])

  // baby: Show success animation when download starts, clear after 3s
  const handleDownloadClick = useCallback((platform: 'mac-arm64' | 'mac-x64') => {
    window.open(`${NINX_DOWNLOAD_API}?platform=${platform}`, '_blank', 'noopener,noreferrer')
    setDownloadSuccess(platform)
    setTimeout(() => setDownloadSuccess(null), 3000)
  }, [])

  const { data: chatAnimationData } = useQuery({
    queryKey: ['ninx', 'home', 'chat-animation'],
    queryFn: fetchAnimationData,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 1000 * 60 * 60
  })

  const { data: platforms } = useQuery({
    queryKey: ['ninx', 'download-platforms'],
    queryFn: fetchNinxPlatforms,
    staleTime: 60 * 1000, // 1 min
    gcTime: 5 * 60 * 1000
  })

  return (
    <div className="min-h-screen bg-[#F6F9FD] text-slate-900 relative overflow-x-hidden font-sans selection:bg-[#21728d]/10">
      <GridPattern />

      {/* baby: Download success toast - checkmark animation */}
      {downloadSuccess && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-3 rounded-xl bg-white border border-slate-200 shadow-lg px-5 py-3 animate-in fade-in slide-in-from-bottom-4 duration-300"
          role="status"
          aria-live="polite"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
            <CheckCircle2 className="h-6 w-6 text-emerald-600 animate-in zoom-in duration-300" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">Download started</p>
            <p className="text-xs text-slate-500">Check your Downloads folder</p>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-6 md:px-8 relative z-50">
        <div className="flex items-center gap-2">
          <Image
            src="/ninx_logo_light.svg"
            alt="NINX"
            width={40}
            height={40}
            className="h-10 w-10"
            priority
          />
          <span className={cn("text-xm font-bold tracking-[0.2em] pt-1", montserratSubrayada.className)}>NINX</span>
        </div>
        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
          <Link href="/nin" className="hover:text-black transition-colors">NIN</Link>
          <Link href="/home" className="hover:text-black transition-colors">Ningenie</Link>
          <button
            type="button"
            onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
            className="text-sm font-medium text-slate-600 hover:text-black transition-colors cursor-pointer bg-transparent border-0"
          >
            Features
          </button>
          <button
            type="button"
            onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
            className="text-sm font-medium text-slate-600 hover:text-black transition-colors cursor-pointer bg-transparent border-0"
          >
            Pricing
          </button>
          <Link href="#docs" className="hover:text-black transition-colors">Docs</Link>
        </div>
        <div className="flex items-center gap-4">
          <Button className="rounded-md bg-[#21728d] text-white hover:bg-slate-800 px-6 h-10" onClick={() => document.getElementById('download')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>
            Download
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="mx-auto max-w-7xl px-6 pt-16 pb-32 text-center md:px-8 relative z-10">

        <div className="flex flex-wrap justify-center gap-3 mb-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">
            <span className="flex h-2 w-2 rounded-full bg-[#21728d]"></span>
            Introducing NINX
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">
            <ShieldCheck className="h-3 w-3 text-[#21728d]" />
            CASA + Certified
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">
            <ShieldCheck className="h-3 w-3 text-[#21728d]" />
            SOC 2 in progress
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">
            <ShieldCheck className="h-3 w-3 text-[#21728d]" />
            ZDR on cloud credits
          </div>
        </div>

        <h1 className="mx-auto max-w-4xl text-5xl font-bold tracking-tight text-slate-900 md:text-7xl mb-6">
          Don&apos;t Ask, Just Task.<br />
          <span className="text-[#21728d]">On Your Desktop.</span>
        </h1>
        <p className="mx-auto max-w-2xl text-lg text-slate-500 mb-12">
          Gmail, Calendar, Brokers, Health, Notion, Contacts, and more...plus local shell, system control, connection to the app, and remote control from anywhere.
        </p>

        {/* Central Visual Stack - The CPU Part */}
        <div className="relative w-full max-w-4xl mx-auto h-[800px]">

          {/* 1. Input Card - Light (same as Ningenie landing) */}
          <div className="relative z-30 mx-auto max-w-xl">
            <div className="relative rounded-[2rem] border border-slate-200 bg-white p-2 shadow-2xl shadow-slate-200/50">
              <div className="flex flex-col gap-3 p-4">
                <div className="relative">
                  <textarea
                    className="rounded-xl w-full resize-none border-0 bg-transparent p-2 text-lg font-medium placeholder:text-slate-300 focus:ring-0 min-h-[60px] focus:outline-none text-center"
                    placeholder="Type your intent..."
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full text-slate-400">
                    <Plus className="h-5 w-5" />
                  </Button>
                  <Button className="rounded-full bg-[#21728d] text-white hover:bg-slate-800 px-6 h-9" onClick={() => document.getElementById('download')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>
                    Execute now <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
              {/* Glow */}
              <div className="absolute -inset-px -z-10 rounded-[2rem] bg-gradient-to-r from-rose-100/50 via-sky-100/50 to-indigo-100/50 opacity-50 blur-lg" />
            </div>
          </div>

          {/* Connecting Lines Vertical */}
          <div className="absolute top-[180px] left-1/2 -translate-x-1/2 h-12 w-px bg-slate-200 z-20" />

          {/* 2. "Connect With" Card - Ningenie apps + Local Resources */}
          <div className="absolute top-[220px] left-1/2 -translate-x-1/2 z-30">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-4 flex flex-col items-center gap-4 min-w-[340px]">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Or connect with</span>
              <div className="flex flex-wrap justify-center gap-2">
                <Button variant="outline" className="rounded-xl bg-white border-slate-200 h-9 gap-2 px-3 hover:bg-slate-50 hover:text-black text-xs">
                  <Mail className="h-3.5 w-3.5 text-slate-500" /> Gmail
                </Button>
                <Button variant="outline" className="rounded-xl bg-white border-slate-200 h-9 gap-2 px-3 hover:bg-slate-50 hover:text-black text-xs">
                  <LineChart className="h-3.5 w-3.5 text-slate-500" /> Brokers
                </Button>
                <Button variant="outline" className="rounded-xl bg-white border-slate-200 h-9 gap-2 px-3 hover:bg-slate-50 hover:text-black text-xs">
                  <Inbox className="h-3.5 w-3.5 text-slate-500" /> Social
                </Button>
                <Button variant="outline" className="rounded-xl bg-white border-slate-200 h-9 gap-2 px-3 hover:bg-slate-50 hover:text-black text-xs">
                  <Terminal className="h-3.5 w-3.5 text-slate-500" /> Shell
                </Button>
                <Button variant="outline" className="rounded-xl bg-white border-slate-200 h-9 gap-2 px-3 hover:bg-slate-50 hover:text-black text-xs">
                  <Monitor className="h-3.5 w-3.5 text-slate-500" /> System
                </Button>
                <Button variant="outline" className="rounded-xl bg-white border-slate-200 h-9 gap-2 px-3 hover:bg-slate-50 hover:text-black text-xs">
                  <Globe className="h-3.5 w-3.5 text-slate-500" /> Connect
                </Button>
              </div>
            </div>
          </div>

          {/* Connecting Lines Vertical */}
          <div className="absolute top-[300px] left-1/2 -translate-x-1/2 h-20 w-px bg-slate-200 z-20" />

          {/* 3. Central Processor Chip - Light (same as Ningenie landing) */}
          <div className="absolute top-[380px] left-1/2 -translate-x-1/2 z-20">
            <div className="relative h-64 w-64 bg-white rounded-[3rem] border border-slate-200 shadow-xl flex items-center justify-center">
              <div className="absolute inset-0 rounded-[3rem] bg-gradient-to-b from-white to-slate-50/50" />

              {/* Decorative Dots - Corners */}
              <div className="absolute top-6 left-6 flex gap-1.5">
                <div className="h-2 w-2 rounded-full bg-slate-200" />
                <div className="h-2 w-2 rounded-full bg-slate-200" />
                <div className="h-2 w-2 rounded-full bg-slate-200" />
              </div>
              <div className="absolute top-6 right-6 flex gap-1.5">
                <div className="h-2 w-2 rounded-full bg-slate-200" />
                <div className="h-2 w-2 rounded-full bg-slate-200" />
                <div className="h-2 w-2 rounded-full bg-slate-200" />
              </div>
              <div className="absolute bottom-6 left-6 flex gap-1.5">
                <div className="h-2 w-2 rounded-full bg-slate-200" />
                <div className="h-2 w-2 rounded-full bg-slate-200" />
                <div className="h-2 w-2 rounded-full bg-slate-200" />
              </div>
              <div className="absolute bottom-6 right-6 flex gap-1.5">
                <div className="h-2 w-2 rounded-full bg-slate-200" />
                <div className="h-2 w-2 rounded-full bg-slate-200" />
                <div className="h-2 w-2 rounded-full bg-slate-200" />
              </div>

              {/* Pins - Sides */}
              <div className="absolute top-1/2 -translate-y-1/2 -left-2 flex flex-col gap-3">
                {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-1.5 bg-slate-300 rounded-full" />)}
              </div>
              <div className="absolute top-1/2 -translate-y-1/2 -right-2 flex flex-col gap-3">
                {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-1.5 bg-slate-300 rounded-full" />)}
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 -top-2 flex gap-3">
                {[1, 2, 3, 4].map(i => <div key={i} className="w-4 h-1.5 bg-slate-300 rounded-full" />)}
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 flex gap-3">
                {[1, 2, 3, 4].map(i => <div key={i} className="w-4 h-1.5 bg-slate-300 rounded-full" />)}
              </div>

              {/* Inner Core (Lottie) */}
              <div className="relative w-32 h-32 bg-white rounded-[2rem] shadow-inner border border-slate-100 flex items-center justify-center overflow-hidden">
                {chatAnimationData ? (
                  <div className="w-[140%] h-[140%] flex items-center justify-center">
                    <Lottie animationData={chatAnimationData} loop={true} autoplay={true} style={{ width: '100%', height: '100%' }} />
                  </div>
                ) : (
                  <div className="h-16 w-16 rounded-full bg-gradient-to-tr from-[#21728d] to-[#88a1ac] shadow-lg animate-pulse" />
                )}
              </div>
            </div>
          </div>

          {/* Circuit SVG Background placed behind chip */}
          <div className="absolute top-[380px] left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full z-10">
            <CircuitBackground />
          </div>

          {/* Flow Explanations (Floating Chips - Scattered) */}

          {/* Left Side - User intents (Ningenie-style) */}
          <div className="absolute top-[340px] left-10 z-30 hidden md:flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-100 shadow-sm opacity-80 hover:opacity-100 transition-opacity">
            <Mail className="h-4 w-4 text-slate-500" />
            <span className="text-xs font-medium text-slate-600">&quot;Organize my emails&quot;</span>
          </div>
          <div className="absolute top-[440px] -left-8 z-30 hidden md:flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-100 shadow-sm opacity-80 hover:opacity-100 transition-opacity">
            <TrendingUp className="h-4 w-4 text-slate-500" />
            <span className="text-xs font-medium text-slate-600">&quot;Check my portfolio&quot;</span>
          </div>
          <div className="absolute top-[540px] left-4 z-30 hidden md:flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-100 shadow-sm opacity-80 hover:opacity-100 transition-opacity">
            <Terminal className="h-4 w-4 text-slate-500" />
            <span className="text-xs font-medium text-slate-600">&quot;Open my projects folder&quot;</span>
          </div>

          {/* Right Side - Agent responses */}
          <div className="absolute top-[350px] right-10 z-30 hidden md:flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-100 shadow-sm opacity-80 hover:opacity-100 transition-opacity">
            <Zap className="h-4 w-4 text-amber-500" />
            <span className="text-xs font-medium text-slate-600">Calling Gmail...</span>
          </div>
          <div className="absolute top-[450px] -right-8 z-30 hidden md:flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-100 shadow-sm opacity-80 hover:opacity-100 transition-opacity">
            <Globe className="h-4 w-4 text-indigo-500" />
            <span className="text-xs font-medium text-slate-600">Syncing with app</span>
          </div>
          <div className="absolute top-[550px] right-4 z-30 hidden md:flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-100 shadow-sm opacity-80 hover:opacity-100 transition-opacity">
            <ShieldCheck className="h-4 w-4 text-emerald-500" />
            <span className="text-xs font-medium text-slate-600">Task completed</span>
          </div>

        </div>

        {/* Download Buttons - Centered */}
        <div id="download" className="flex flex-col gap-8 justify-center items-center mt-1 mb-60 z-30 relative">
          <div className="flex flex-col gap-4 sm:flex-row">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center justify-between gap-3 rounded-xl bg-slate-900 px-6 py-4 text-white shadow-lg shadow-slate-900/20 transition hover:bg-slate-800 hover:-translate-y-0.5 min-w-[200px]"
                >
                  <div className="flex items-center gap-3">
                    <Apple className="h-6 w-6" />
                    <div className="flex flex-col items-start">
                      <span className="text-[10px] leading-none text-slate-400 uppercase tracking-wider">Download for</span>
                      <span className="text-base font-bold leading-tight">macOS</span>
                    </div>
                  </div>
                  <ChevronDown className="h-4 w-4 shrink-0 opacity-70" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 p-2 bg-white border-slate-200 shadow-lg" sideOffset={8}>
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    className={cn(
                      "flex flex-col gap-0.5 rounded-lg px-3 py-2.5 text-left transition w-full",
                      platforms?.macArm64 !== false
                        ? "hover:bg-slate-100 text-slate-900 cursor-pointer"
                        : "opacity-50 cursor-not-allowed pointer-events-none"
                    )}
                    onClick={() => platforms?.macArm64 !== false && handleDownloadClick('mac-arm64')}
                  >
                    <span className="text-sm font-semibold">Mac Silicon</span>
                    <span className="text-xs text-slate-500">M1, M2, M3, M4 chips</span>
                  </button>
                  {platforms?.macX64 ? (
                    <button
                      type="button"
                      className="flex flex-col gap-0.5 rounded-lg px-3 py-2.5 text-left transition hover:bg-slate-100 text-slate-900 w-full cursor-pointer"
                      onClick={() => handleDownloadClick('mac-x64')}
                    >
                      <span className="text-sm font-semibold">Mac x64</span>
                      <span className="text-xs text-slate-500">Intel chips</span>
                    </button>
                  ) : (
                    <div className="flex flex-col gap-0.5 rounded-lg px-3 py-2.5 text-left opacity-60">
                      <span className="text-sm font-semibold text-slate-600">Mac x64</span>
                      <span className="text-xs text-slate-500">Intel chips · Coming soon</span>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            <Link
              href="#"
              className="inline-flex items-center gap-3 rounded-xl bg-white border border-slate-200 px-6 py-4 text-slate-900 shadow-sm transition hover:bg-slate-50 hover:-translate-y-0.5 min-w-[200px]"
            >
              <AppWindow className="h-6 w-6" />
              <div className="flex flex-col items-start">
                <span className="text-[10px] leading-none text-slate-400 uppercase tracking-wider">Coming soon</span>
                <span className="text-base font-bold leading-tight">Windows</span>
              </div>
            </Link>
          </div>

          {/* Other products - App buttons, less focus */}
          <div className="flex flex-col items-center gap-2">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Other products</span>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                href="https://apps.apple.com/app/ningenie-dont-ask-just-task/id6755959217"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white/80 px-4 py-2.5 text-slate-600 shadow-sm transition hover:bg-slate-50 hover:border-slate-300 hover:text-slate-800 min-w-[120px]"
              >
                <Apple className="h-4 w-4 text-slate-500 shrink-0" />
                <div className="flex flex-col items-start">
                  <span className="text-[10px] leading-none text-slate-400">Download on the</span>
                  <span className="text-sm font-medium leading-tight">App Store</span>
                </div>
              </Link>
              <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/80 px-4 py-2.5 text-slate-400 cursor-not-allowed min-w-[120px]">
                <Smartphone className="h-4 w-4 shrink-0" />
                <div className="flex flex-col items-start">
                  <span className="text-[10px] leading-none">Coming Soon</span>
                  <span className="text-sm font-medium leading-tight">Android</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trusted / Connect Section */}
        <div className="relative mt-12 max-w-5xl mx-auto pt-16 pb-16 border-t border-slate-100">
          {/* Corner Markers - same as Ningenie landing */}
          <div className="absolute top-0 left-0 w-4 h-4 border-l border-t border-slate-300" />
          <div className="absolute top-0 right-0 w-4 h-4 border-r border-t border-slate-300" />
          <div className="absolute bottom-0 left-0 w-4 h-4 border-l border-b border-slate-300" />
          <div className="absolute bottom-0 right-0 w-4 h-4 border-r border-b border-slate-300" />
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-12 text-center">Trusted by 200+ partners</p>
          <div className="flex flex-wrap justify-center gap-12 md:gap-24 grayscale opacity-60">
            <div className="flex items-center gap-2 font-bold text-xl text-slate-800"><Mail className="h-6 w-6" /> Gmail</div>
            <div className="flex items-center gap-2 font-bold text-xl text-slate-800"><LineChart className="h-6 w-6" /> Brokers</div>
            <div className="flex items-center gap-2 font-bold text-xl text-slate-800"><Globe className="h-6 w-6" /> Kernal</div>
            <div className="flex items-center gap-2 font-bold text-xl text-slate-800"><Wallet className="h-6 w-6" /> Axis</div>
          </div>
        </div>

      </main>

      {/* Features Section */}
      <section id="features" className="py-24 bg-slate-50/50 border-t border-slate-100 relative overflow-hidden">
        <GridPattern />
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-16 px-6 md:px-10 lg:px-16 relative z-10">
          <div className="flex flex-col gap-4">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 w-fit shadow-sm">
              <span className="flex h-2 w-2 rounded-full bg-[#21728d]"></span>
              Live Demonstrations
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 md:text-5xl">See NINX in action</h2>
            <p className="max-w-3xl text-lg text-slate-500">
              Gmail, Calendar, Brokers, Health, Notion, Contacts, and more: plus local shell, system control, connection to the app, and remote control from anywhere.
            </p>
          </div>

          <FeatureCarousel />
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-slate-50/50 border-t border-slate-100">
        <div className="mx-auto max-w-6xl px-6 md:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 mb-6 shadow-sm">
              <span className="flex h-2 w-2 rounded-full bg-[#ff5500]"></span>
              Pricing
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Flexible Pricing</h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">Explore simple, transparent pricing built for real-world AI workloads.</p>

            <div className="flex items-center justify-center gap-3 mt-8">
              <span className="text-sm font-medium text-slate-600">Monthly</span>
              <Switch />
              <span className="text-sm font-medium text-slate-900">Yearly <span className="text-[#21728d] text-xs ml-1 font-normal">(-20%)</span></span>
            </div>
          </div>

          <div className="grid gap-6 items-stretch md:grid-cols-3">
            {[
              {
                name: "Free",
                price: "$0",
                desc: "A simple way to explore the platform.",
                credits: "20 credits",
                cta: "Get Started",
                details: ["Basic Plugins", "Up to 10 Automations", "Community support"],
              },
              {
                name: "Pro",
                price: "$4.85",
                desc: "Smart automation for solo users.",
                credits: "420 credits",
                popular: true,
                cta: "Get Pro",
                details: ["Basic Plugins", "Up to 400 Automations", "Community support"],
              },
              {
                name: "Pro+",
                price: "$18.99",
                desc: "Premium workflows with full plugin access.",
                credits: "2,000 credits",
                cta: "Get Pro+",
                details: ["Full Plugins", "NIN Crew", "Up to 2,000 Automations", "Dedicated support"],
              },
            ].map((tier, i) => (
              <Card key={i} className={cn("relative flex h-full flex-col border-slate-200 bg-white transition-all hover:-translate-y-1 hover:shadow-lg", tier.popular && "border-[#21728d] shadow-md")}>
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#21728d] text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">Most Popular</div>
                )}
                <CardHeader className="pb-4">
                  <h3 className="font-semibold text-slate-900">{tier.name}</h3>
                  <p className="text-xs text-slate-500 mt-1 min-h-[40px]">{tier.desc}</p>
                </CardHeader>
                <CardContent className="flex flex-1 flex-col">
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-3xl font-bold text-slate-900">{tier.price}</span>
                    {tier.price !== "$0" && <span className="text-slate-500 text-sm">/month</span>}
                  </div>
                  <Button
                    className={cn("w-full mb-6", tier.popular ? "bg-black text-white hover:bg-slate-800" : "bg-white border border-slate-200 text-slate-900 hover:bg-slate-50")}
                    onClick={goToPlansPage}
                  >
                    {tier.cta}
                  </Button>
                  <div className="space-y-3 flex-1">
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <div className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                      {tier.credits}
                    </div>
                    {tier.details.map((detail) => (
                      <div key={detail} className="flex items-center gap-2 text-xs text-slate-600">
                        <div className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                        {detail}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <div className="inline-flex items-center justify-center p-2 bg-emerald-100/50 rounded-full mb-4">
            <div className="h-2 w-2 rounded-full bg-emerald-500" />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-12">Common Questions</h2>

          <div className="text-left space-y-4 overflow-hidden relative z-20">
            {FAQ_ITEMS.map((item, i) => {
              const isOpen = openFaqIndex === i;
              return (
                <div
                  key={i}
                  className="border-b border-slate-100 transition-all duration-300 bg-white"
                >
                  <button
                    onClick={() => setOpenFaqIndex(isOpen ? null : i)}
                    className="flex w-full items-center justify-between p-4 hover:bg-slate-50 transition-colors cursor-pointer group select-none text-left"
                    aria-expanded={isOpen}
                  >
                    <span className={cn(
                      "font-medium transition-colors",
                      isOpen ? "text-[#21728d]" : "text-slate-700 group-hover:text-black"
                    )}>
                      {item.question}
                    </span>
                    <Plus className={cn(
                      "h-4 w-4 text-slate-400 group-hover:text-black transition-transform duration-300",
                      isOpen && "rotate-45 text-[#21728d]"
                    )} />
                  </button>

                  <div
                    className={cn(
                      "transition-all duration-500 ease-in-out overflow-hidden text-slate-600",
                      isOpen ? "max-h-[300px] opacity-100 pb-5 px-4" : "max-h-0 opacity-0"
                    )}
                  >
                    <div className="pt-1 leading-relaxed">
                      {item.answer}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-white border-t border-slate-100 relative overflow-hidden">
        <GridPattern />
        <div className="mx-auto max-w-4xl px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 mb-8 shadow-sm">
            <span className="flex h-2 w-2 rounded-full bg-[#21728d]"></span>
            Introducing NINX
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">What to execute next?</h2>
          <p className="text-lg text-slate-500 mb-10">Download NINX: powerful agent on your desktop, plus local shell, system control, and remote access.</p>
          <Button className="rounded-full bg-[#21728d] text-white hover:bg-slate-800 px-8 h-12 text-base font-bold" onClick={() => document.getElementById('download')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>
            Download NINX
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-12 border-t border-slate-100">
        <div className="mx-auto max-w-7xl px-6 md:px-8 flex flex-col md:flex-row justify-between gap-8 text-xs text-slate-500">
          <div className="flex gap-8">
            <Link href="/nin" className="hover:text-slate-900 cursor-pointer touch-manipulation">NIN</Link>
            <Link href="/home" className="hover:text-slate-900 cursor-pointer touch-manipulation">Ningenie App</Link>
            <Link href="#" className="hover:text-slate-900 cursor-pointer touch-manipulation">Documentation</Link>
            <Link href="#" className="hover:text-slate-900 cursor-pointer touch-manipulation">GitHub</Link>
            <Link href="/ningenie/policies/terms-of-service" className="hover:text-slate-900 cursor-pointer touch-manipulation">Terms</Link>
            <Link href="/ningenie/policies/privacy-policy" className="hover:text-slate-900 cursor-pointer touch-manipulation">Privacy</Link>
          </div>
          <div className="flex gap-4 items-center">
            <span>© 2026 NINX. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default memo(NinxLandingPage)
